import React, { useState, useEffect, useCallback } from "react";
import { fetchDynamics, fetchEmployees } from "../api";
import Loading from "../components/Loading";
import ErrorRetry from "../components/ErrorRetry";
import { formatWeekDate } from "../utils/formatWeek";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area, BarChart, Bar
} from "recharts";

const iconProps = {
  width: 22,
  height: 22,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

const WorkloadIcon = () => (
  <svg {...iconProps}>
    <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
  </svg>
);

const DeadlinesIcon = () => (
  <svg {...iconProps}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);

const CommunicationIcon = () => (
  <svg {...iconProps}>
    <path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.5 8.5 0 0 1-3.8-.9L3 21l1.9-5.7a8.5 8.5 0 0 1-.9-3.8 8.38 8.38 0 0 1 8.5-8.5A8.38 8.38 0 0 1 21 11.5z" />
  </svg>
);

const EnergyIcon = () => (
  <svg {...iconProps}>
    <path d="M13 2 4 14h7l-1 8 9-12h-7l1-8z" />
  </svg>
);

const METRICS = [
  { 
    key: "avg_workload", 
    label: "Нагрузка", 
    color: "#38b6aa",
    icon: WorkloadIcon,
    description: "Уровень рабочей нагрузки"
  },
  { 
    key: "avg_deadlines", 
    label: "Дедлайны", 
    color: "#5c87b0",
    icon: DeadlinesIcon,
    description: "Соблюдение сроков"
  },
  { 
    key: "avg_communication", 
    label: "Коммуникация", 
    color: "#c08a7f",
    icon: CommunicationIcon,
    description: "Качество коммуникации"
  },
  { 
    key: "avg_energy", 
    label: "Энергия", 
    color: "#5aa469",
    icon: EnergyIcon,
    description: "Уровень энергии и мотивации"
  },
];

function formatWeek(iso) {
  return formatWeekDate(iso);
}

function getTrend(data, key) {
  if (data.length < 2) return null;
  const current = data[data.length - 1][key];
  const previous = data[data.length - 2][key];
  if (current == null || previous == null) return null;
  const diff = ((current - previous) / previous * 100).toFixed(1);
  return { diff: Math.abs(diff), up: current >= previous };
}

function calculateAverage(data, key) {
  const values = data.map(d => d[key]).filter(v => v != null);
  if (values.length === 0) return null;
  return (values.reduce((a, b) => a + b, 0) / values.length).toFixed(2);
}

// Custom Tooltip
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border-color)',
        borderRadius: '8px',
        padding: '16px',
        boxShadow: 'var(--shadow-md)'
      }}>
        <p style={{ 
          margin: '0 0 12px 0', 
          fontWeight: 700,
          color: 'var(--text-primary)',
          fontSize: '14px'
        }}>
          {label}
        </p>
        {payload.map((entry, index) => (
          <div key={index} style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '8px',
            fontSize: '13px'
          }}>
            <div style={{
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              background: entry.color
            }} />
            <span style={{ color: 'var(--text-secondary)' }}>{entry.name}:</span>
            <span style={{ 
              color: 'var(--text-primary)', 
              fontWeight: 600,
              marginLeft: 'auto'
            }}>
              {entry.value?.toFixed(2) || "—"}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

// Metric Summary Card
function MetricCard({ metric, data }) {
  const avg = calculateAverage(data, metric.key);
  const trend = getTrend(data, metric.key);
  const Icon = metric.icon;

  return (
    <div className="metric-card animate-fade-in">
      <div className="metric-icon" style={{ 
        background: `${metric.color}20`,
        color: metric.color
      }}>
        <Icon />
      </div>
      <div className="metric-label">{metric.label}</div>
      <div className="metric-value" style={{ color: metric.color }}>
        {avg || "—"}
      </div>
      {trend && (
        <div style={{
          fontSize: '12px',
          marginTop: '4px',
          color: trend.up ? 'var(--score-high)' : 'var(--score-low)',
          fontWeight: 600
        }}>
          {trend.up ? '↑' : '↓'} {trend.diff}%
        </div>
      )}
    </div>
  );
}

export default function Dynamics() {
  const [data, setData] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [chartType, setChartType] = useState("line");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadEmployees = useCallback(() => {
    fetchEmployees()
      .then(setEmployees)
      .catch((err) => {
        console.error("Failed to load employees:", err);
        setError("Не удалось загрузить список сотрудников");
      });
  }, []);

  const loadDynamics = useCallback(() => {
    setError(null);
    setLoading(true);
    fetchDynamics(selectedEmployee || undefined)
      .then(setData)
      .catch((err) => {
        console.error("Failed to load dynamics:", err);
        setError("Не удалось загрузить данные динамики");
      })
      .finally(() => setLoading(false));
  }, [selectedEmployee]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  useEffect(() => {
    loadDynamics();
  }, [loadDynamics]);

  const chartData = (data.length > 0 ? data : [{
    week_start: "",
    avg_workload: null,
    avg_deadlines: null,
    avg_communication: null,
    avg_energy: null,
    count: 0,
  }]).map((d) => ({
    ...d,
    month_label: d.week_start ? formatWeek(d.week_start) : "—",
    week_label: d.week_start ? formatWeek(d.week_start) : "—",
  }));

  if (error) return <ErrorRetry message={error} onRetry={loadDynamics} />;
  if (loading && data.length === 0) return <Loading text="Загрузка динамики..." />;

  const renderChart = () => {
    const commonProps = {
      data: chartData,
      margin: { top: 20, right: 30, left: 0, bottom: 20 }
    };

    const commonAxis = (
      <>
        <CartesianGrid 
          strokeDasharray="3 3" 
          stroke="var(--border-color)"
          vertical={false}
        />
        <XAxis 
          dataKey="week_label" 
          stroke="var(--text-muted)"
          fontSize={12}
          tickLine={false}
          axisLine={{ stroke: 'var(--border-color)' }}
        />
        <YAxis 
          domain={[1, 5]} 
          stroke="var(--text-muted)"
          fontSize={12}
          tickLine={false}
          axisLine={{ stroke: 'var(--border-color)' }}
          ticks={[1, 2, 3, 4, 5]}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend 
          wrapperStyle={{ paddingTop: '20px' }}
          iconType="circle"
        />
      </>
    );

    switch (chartType) {
      case 'area':
        return (
          <AreaChart {...commonProps}>
            {commonAxis}
            {METRICS.map((m) => (
              <Area
                key={m.key}
                type="monotone"
                dataKey={m.key}
                name={m.label}
                stroke={m.color}
                fill={m.color}
                fillOpacity={0.1}
                strokeWidth={2}
                dot={{ r: 3, strokeWidth: 2, fill: 'var(--bg-card)' }}
                activeDot={{ r: 6, strokeWidth: 0 }}
              />
            ))}
          </AreaChart>
        );
      case 'bar':
        return (
          <BarChart {...commonProps}>
            {commonAxis}
            {METRICS.map((m, i) => (
              <Bar
                key={m.key}
                dataKey={m.key}
                name={m.label}
                fill={m.color}
                radius={[4, 4, 0, 0]}
                maxBarSize={40}
              />
            ))}
          </BarChart>
        );
      default:
        return (
          <LineChart {...commonProps}>
            {commonAxis}
            {METRICS.map((m) => (
              <Line
                key={m.key}
                type="monotone"
                dataKey={m.key}
                name={m.label}
                stroke={m.color}
                strokeWidth={3}
                dot={{ r: 4, strokeWidth: 2, fill: 'var(--bg-card)' }}
                activeDot={{ r: 7, strokeWidth: 0 }}
              />
            ))}
          </LineChart>
        );
    }
  };

  return (
    <div className="dynamics animate-fade-in">
      {/* Header */}
      <div className="section-header">
        <div>
          <h2>Динамика по времени</h2>
          <div className="section-subtitle">Средние оценки команды по неделям</div>
        </div>
        <div className="controls">
          <div className="toolbar-segment">
            {['line', 'area', 'bar'].map(type => (
              <button
                key={type}
                className={chartType === type ? "active" : ""}
                onClick={() => setChartType(type)}
              >
                {type === 'line' ? 'Линии' : type === 'area' ? 'Области' : 'Столбцы'}
              </button>
            ))}
          </div>
          <select
            value={selectedEmployee}
            onChange={(e) => setSelectedEmployee(e.target.value)}
          >
            <option value="">Все сотрудники</option>
            {employees.map((e) => (
              <option key={e.id} value={e.id}>
                {e.full_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Metrics Summary */}
      <div className="metrics-grid" style={{ marginBottom: '32px' }}>
        {METRICS.map((metric) => (
          <MetricCard key={metric.key} metric={metric} data={data} />
        ))}
      </div>

      {/* Chart */}
      <div className="data-card">
        <div className="data-card-header">
          <div className="data-card-title">
            График динамики
          </div>
          <span style={{ 
            fontSize: '13px', 
            color: 'var(--text-muted)',
            fontWeight: 500 
          }}>
            {data.length} периодов
          </span>
        </div>
        <div className="data-card-body">
          <ResponsiveContainer width="100%" height={400}>
            {renderChart()}
          </ResponsiveContainer>
        </div>
      </div>

      {/* Data Table */}
      <div className="data-card" style={{ marginTop: '32px' }}>
        <div className="data-card-header">
          <div className="data-card-title">
            Данные по периодам
          </div>
        </div>
        <div className="data-card-body" style={{ padding: 0 }}>
          <div className="table-container">
            <table className="score-table">
              <thead>
                <tr>
                  <th>Период</th>
                  {METRICS.map(m => (
                    <th key={m.key}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>{m.label}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...chartData].reverse().map((row, i) => (
                  <tr 
                    key={row.week_start}
                    className="animate-fade-in"
                    style={{ animationDelay: `${i * 0.05}s` }}
                  >
                    <td style={{ fontWeight: 600 }}>{row.week_label}</td>
                    {METRICS.map(m => {
                      const value = row[m.key];
                      let color = 'var(--text-primary)';
                      if (value != null) {
                        if (value <= 2) color = 'var(--score-low)';
                        else if (value === 3) color = 'var(--score-medium)';
                        else color = 'var(--score-high)';
                      }
                      return (
                        <td key={m.key}>
                          <span style={{ 
                            fontWeight: 700, 
                            color,
                            fontSize: '15px'
                          }}>
                            {value?.toFixed(2) || "—"}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
